import { Eye } from "lucide-react";

import { LegalDocumentContent } from "@/components/legal/LegalDocumentContent";
import { AppCard } from "@/components/ui/AppCard";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Typography } from "@/components/ui/Typography";
import { cn } from "@/lib/utils";

/** Fixed workspace height for document detail edit layout (viewport minus admin chrome). */
export const LEGAL_DOCUMENT_WORKSPACE_MIN_HEIGHT = "h-[calc(100vh-9rem)] max-h-[calc(100vh-9rem)]";

type TLegalDocumentPreviewContentProps = {
  title?: string;
  content: string;
  version?: string;
  effectiveAt?: string;
};

export function LegalDocumentPreviewContent({
  title,
  content,
  version,
  effectiveAt,
}: TLegalDocumentPreviewContentProps) {
  const effectiveDate = effectiveAt
    ? new Date(effectiveAt).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <>
      {title ? (
        <Typography variant="h4" className="mb-2 tracking-tight">
          {title}
        </Typography>
      ) : null}

      {(effectiveDate ?? version) ? (
        <Typography variant="p" affects="muted" className="mb-4 text-sm">
          {effectiveDate ? `Effective ${effectiveDate}` : null}
          {effectiveDate && version ? " — " : null}
          {version ? `Version ${version}` : null}
        </Typography>
      ) : null}

      <LegalDocumentContent content={content} />
    </>
  );
}

type TLegalDocumentPreviewProps = TLegalDocumentPreviewContentProps & {
  className?: string;
};

/** Inline card preview (optional; detail page uses dialog). */
export function LegalDocumentPreview({ title, content, version, effectiveAt, className }: TLegalDocumentPreviewProps) {
  return (
    <AppCard
      variant="elevated"
      padding="none"
      className={cn("flex flex-col overflow-hidden", LEGAL_DOCUMENT_WORKSPACE_MIN_HEIGHT, className)}
    >
      <div className="shrink-0 border-b border-border bg-muted/30 px-5 py-4 sm:px-6">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Eye className="h-4 w-4" />
          </div>
          <div className="min-w-0 space-y-0.5">
            <Typography variant="h5">Live preview</Typography>
            <Typography variant="p" affects="muted" className="text-sm">
              Matches the published legal page layout.
            </Typography>
          </div>
        </div>
      </div>

      <div className="legal-document-scroll flex min-h-0 flex-1 flex-col overflow-y-auto bg-card px-5 py-5 sm:px-6 sm:py-6">
        <LegalDocumentPreviewContent
          title={title}
          content={content}
          version={version}
          effectiveAt={effectiveAt}
        />
      </div>
    </AppCard>
  );
}

type TLegalDocumentPreviewDialogProps = TLegalDocumentPreviewContentProps & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function LegalDocumentPreviewDialog({
  open,
  onOpenChange,
  title,
  content,
  version,
  effectiveAt,
}: TLegalDocumentPreviewDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col gap-0 overflow-hidden p-0 sm:rounded-2xl">
        <DialogHeader className="space-y-1 border-b border-border px-6 py-4 text-left">
          <DialogTitle className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Eye className="h-4 w-4" />
            </span>
            Live preview
          </DialogTitle>
          <DialogDescription>Matches the published legal page layout.</DialogDescription>
        </DialogHeader>
        <div className="legal-document-scroll min-h-0 flex-1 overflow-y-auto px-6 py-6">
          <LegalDocumentPreviewContent
            title={title}
            content={content}
            version={version}
            effectiveAt={effectiveAt}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
